// GradeHurdleAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * GradeHurdleAdmin
 * -----------------------------------------------------------------
 * Props:
 *   - platformUrl: e.g., https://platform-extensions-int.blackboard.com
 *   - backEndUrl:  e.g., https://your-backend.example.com
 *   - locale:      optional i18n key (default "en_US")
 *
 * URL params expected:
 *   - ?token=... (OAuth bearer for Learn calls)
 *
 * Backend (this implementation expects these routes; with fallbacks):
 *   - GET    {backEndUrl}/grade-hurdle-associations
 *   - PATCH  {backEndUrl}/grade-hurdle-associations            body: { CoursePk1, Status }
 *   - PUT    {backEndUrl}/grade-hurdle-associations            body: { CoursePk1, Status }
 *   - DELETE {backEndUrl}/grade-hurdle-associations            body: { CoursePk1 }
 *   - (fallback)
 *       POST {backEndUrl}/grade-hurdle-associations           body: [{ CoursePk1, Status }]  (bulk upsert)
 *
 * Learn (search):
 *   - GET /learn/api/public/v3/courses?name={query}
 *   - GET /learn/api/public/v3/courses?courseId={query}
 *   (merge both result sets; de-duplicate by id)
 *
 * Learn (detail for PK1 → name/courseId display):
 *   - GET /learn/api/public/v1/courses/{coursePk1}
 *
 * Learn (grade notations – ensure on save for NEW courses):
 *   - GET  /learn/api/public/v1/courses/{courseId}/gradebook/gradeNotations
 *   - POST /learn/api/public/v1/courses/{courseId}/gradebook/gradeNotations
 * ----------------------------------------------------------------*/

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The Grade Hurdle Admin Tool is not available for your role.",
        },
        headings: {
            current: "Current Grade Hurdle Associations",
            add: "Add Association (search by Course Name or Course ID)",
            error: "Error",
        },
        buttons: {
            refresh: "Refresh",
            saveChanges: "Save Changes",
            saved: "Saved",
            hide: "Hide",
            show: "Show",
            search: "Search",
            searching: "Searching…",
            clear: "Clear",
            remove: "Remove",
            addActive: "Add (Active)",
            added: "Added",
            retry: "Retry",
        },
        table: {
            name: "Course (name)",
            id: "Course ID",
            status: "Status",
            actions: "Actions",
            availability: "Availability",
            noRows: "No associations yet. Add one below.",
            noResults: "No results",
        },
        status: { active: "Active", inactive: "Inactive" },
        notices: {
            loading: "Loading GradeHurdleAdmin…",
            savedAll: "All changes saved.",
            savedWarn: "Saved with warnings.",
            removed: "Association removed.",
            failedLoad: "Failed to load associations.",
            failedRemove: "Failed to remove association.",
            failedSave: "Failed to save associations.",
            ensureNotations: (n) => `Created ${n} grade notation(s) for new courses.`,
        },
        search: {
            placeholder:
                "Search by name or courseId (e.g., 'Intro Biology' or QE_TEST_003U)",
            failed: "Search failed.",
        },
        confirm: {
            remove: "Remove this association? This can’t be undone.",
        },
        roleId: "GRADE_HURDLE_ADMIN",
    },
    en_AU: {},
};
const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Learn API adapter ---------- */
function makeLearnApi(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const v1 = "/learn/api/public/v1";
    const v3 = "/learn/api/public/v3";

    async function listCoursesV3(params = {}) {
        const results = [];
        const firstResp = await http.get(`${v3}/courses`, { params });
        const first = Array.isArray(firstResp.data) ? firstResp.data : firstResp.data?.results || [];
        results.push(...first);

        let next = firstResp.data?.paging?.nextPage || null;
        while (next) {
            const { data: more } = await http.get(next);
            const chunk = Array.isArray(more) ? more : more?.results || [];
            results.push(...chunk);
            next = more?.paging?.nextPage || null;
        }
        return results;
    }

    async function listGradeNotations(courseId) {
        const { data } = await http.get(`${v1}/courses/${courseId}/gradebook/gradeNotations`);
        return Array.isArray(data) ? data : data?.results || data || [];
    }
    async function createGradeNotation(courseId, payload) {
        const { data } = await http.post(`${v1}/courses/${courseId}/gradebook/gradeNotations`, payload);
        return data;
    }
    async function getCurrentUserInstitutionRoles() {
        const { data } = await http.get(`${v1}/users/me`);
        return data?.institutionRoleIds || [];
    }

    return {
        searchCoursesByNameV3: (name) => listCoursesV3({ name }),
        searchCoursesByCourseIdV3: (courseId) => listCoursesV3({ courseId }),
        getCourseByPk1: async (coursePk1) => {
            const { data } = await http.get(`${v1}/courses/${coursePk1}`);
            return data;
        },
        listGradeNotations,
        createGradeNotation,
        getCurrentUserInstitutionRoles,
    };
}

/** ---------- Backend API adapter ---------- */
function makeBackendApi(backEndUrl) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({ baseURL: base, timeout: 25000, headers: { Accept: "application/json" } });

    return {
        loadAssociations: async () => {
            const { data } = await http.get(`/grade-hurdle-associations`);
            if (Array.isArray(data?.associations)) return data.associations;
            if (Array.isArray(data?.items)) return data.items;
            if (Array.isArray(data)) return data;
            return [];
        },

        patchAssociationStatus: async (coursePk1, status) => {
            try {
                await http.patch(`/grade-hurdle-associations`, { CoursePk1: coursePk1, Status: status });
            } catch (err) {
                const code = err?.response?.status;
                if (code === 404 || code === 405) {
                    await http.post(`/grade-hurdle-associations`, [{ CoursePk1: coursePk1, Status: status }]);
                } else {
                    throw err;
                }
            }
        },

        putAssociation: async (coursePk1, status) => {
            const payload = { CoursePk1: coursePk1, Status: status || "Active" };
            try {
                await http.put(`/grade-hurdle-associations`, payload);
            } catch (err) {
                const code = err?.response?.status;
                if (code === 404 || code === 405) {
                    await http.post(`/grade-hurdle-associations`, [payload]);
                } else {
                    throw err;
                }
            }
        },

        deleteAssociation: async (coursePk1) => {
            await http.delete(`/grade-hurdle-associations`, { data: { CoursePk1: coursePk1 } });
        },
    };
}

/** ---------- Utilities ---------- */
const normalizeStatus = (s) => (String(s || "").toLowerCase() === "active" ? "Active" : "Inactive");
const uniqBy = (arr, keyFn) => {
    const seen = new Set();
    const out = [];
    for (const item of arr) {
        const k = keyFn(item);
        if (!seen.has(k)) {
            seen.add(k);
            out.push(item);
        }
    }
    return out;
};

const REQUIRED_NOTATIONS = [
    { code: "1xx", description: "Grade Hurdle Adjust to 1" },
    { code: "2xx", description: "Grade Hurdle Adjust to 2" },
    { code: "3xx", description: "Grade Hurdle Adjust to 3" },
];

/** ---------- Component ---------- */
const GradeHurdleAdmin = ({ platformUrl, backEndUrl, locale = "en_US" }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = queryParams.get("token") || "";

    const learn = useMemo(() => makeLearnApi(platformUrl, token), [platformUrl, token]);
    const backend = useMemo(() => makeBackendApi(backEndUrl), [backEndUrl]);

    const [loading, setLoading] = useState(true);
    const [fatalError, setFatalError] = useState("");
    const [inlineError, setInlineError] = useState("");
    const [inlineSuccess, setInlineSuccess] = useState("");

    const [associations, setAssociations] = useState([]); // [{ coursePk1, status }]
    const [courseInfo, setCourseInfo] = useState({}); // { [pk1]: { name, courseId } }
    const [dirty, setDirty] = useState(false);

    const initialAssociationsMapRef = useRef(new Map()); // pk1 -> status
    const initialCoursePk1SetRef = useRef(new Set()); // pk1 set

    const [openAdd, setOpenAdd] = useState(true);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchError, setSearchError] = useState("");

    // Institution role gate
    const [roleChecked, setRoleChecked] = useState(false);
    const [hasAdminRole, setHasAdminRole] = useState(false);

    // Compact UI
    const compactCss = `
  .bb-container { font-size: 13px; }
  .bb-card h2 { font-size: 16px; font-weight: 700 !important; }

  .bb-table { font-size: 12px; border-collapse: collapse; width: 100%; }
  .bb-table th, .bb-table td { padding: 6px 8px; border-bottom: 1px solid #eee; }
  .bb-table th { font-weight: 600; text-align: left; }

  .bb-card .bb-button,
  .bb-container .bb-button {
    -webkit-appearance: none !important;
    appearance: none !important;
    background: #f6f6f6 !important;
    border: 1px solid #d3d3d3 !important;
    color: #222 !important;
    border-radius: 6px !important;
    padding: 6px 10px !important;
    font: inherit !important;
    line-height: 1.2 !important;
    text-shadow: none !important;
    box-shadow: none !important;
    cursor: pointer !important;
  }
  .bb-card .bb-button:hover,
  .bb-container .bb-button:hover { background: #efefef !important; }
  .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }

  .bb-card .bb-button.danger { background: #fff1f1 !important; border-color: #e6b3b3 !important; color: #7a0a0a !important; }
  .bb-card .bb-button.danger:hover { background: #fee !important; }
  .bb-card .bb-button.secondary { background: #f2f2f2 !important; border-color: #d0d0d0 !important; color: #222 !important; }

  select, input { font-size: 12px; padding: 4px 6px; height: 30px; font-weight: 400; color: inherit; }
  .bb-select { font-size: 12px; height: 30px; padding: 2px 6px; font-weight: 400; color: inherit; }

  .bb-row { display: flex; gap: 8px; align-items: center; }
`;

    const Flash = ({ type, children }) => {
        if (!children) return null;
        const styles = {
            marginBottom: 6,
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid",
            background: type === "error" ? "#ffecec" : "#ecfff1",
            borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
            color: type === "error" ? "#a40000" : "#005c2e",
            fontSize: 12,
        };
        return (
            <div role={type === "error" ? "alert" : "status"} style={styles} aria-live="polite">
                {children}
            </div>
        );
    };

    // Role check on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!platformUrl || !token) {
                    if (!cancelled) {
                        setHasAdminRole(false);
                        setRoleChecked(true);
                    }
                    return;
                }
                const roles = await learn.getCurrentUserInstitutionRoles();
                if (!cancelled) {
                    setHasAdminRole(Array.isArray(roles) && roles.includes(M.roleId));
                    setRoleChecked(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAdminRole(false);
                    setRoleChecked(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [learn, platformUrl, token, M.roleId]);

    // Load associations + course names/IDs
    async function loadAll({ clearMessages = false } = {}) {
        setLoading(true);
        setFatalError("");
        if (clearMessages) {
            setInlineError("");
            setInlineSuccess("");
        }
        try {
            const list = await backend.loadAssociations();
            const normalized = (list || [])
                .map((a) => ({
                    coursePk1: a.coursePk1 || a.coursePk || a.id || a.pk1 || a.CoursePk1,
                    status: normalizeStatus(a.status || a.Status),
                }))
                .filter((a) => a.coursePk1);

            setAssociations(normalized);
            setDirty(false);

            // Seed initial state for diffing
            const map = new Map();
            for (const a of normalized) map.set(a.coursePk1, normalizeStatus(a.status));
            initialAssociationsMapRef.current = map;
            initialCoursePk1SetRef.current = new Set(normalized.map((a) => a.coursePk1));

            // Populate course display info
            const infoMap = {};
            for (const a of normalized) {
                try {
                    const c = await learn.getCourseByPk1(a.coursePk1);
                    infoMap[a.coursePk1] = { name: c?.name || c?.title || "(untitled course)", courseId: c?.courseId || "" };
                } catch {
                    infoMap[a.coursePk1] = { name: "(unavailable)", courseId: "" };
                }
            }
            setCourseInfo(infoMap);
        } catch (err) {
            setFatalError(err?.response?.data?.message || err.message || M.notices.failedLoad);
        } finally {
            setLoading(false);
        }
    }

    // Initial load after gate
    useEffect(() => {
        if (roleChecked && hasAdminRole) loadAll({ clearMessages: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleChecked, hasAdminRole, backEndUrl, platformUrl, token]);

    // Ensure required Grade Notations for a course (by pk1)
    async function ensureRequiredNotations(coursePk1) {
        try {
            const existing = await learn.listGradeNotations(coursePk1);
            const existingCodes = new Set((existing || []).map((n) => (n?.code || "").trim()));
            const toCreate = REQUIRED_NOTATIONS.filter((n) => !existingCodes.has(n.code));
            for (const n of toCreate) {
                try {
                    await learn.createGradeNotation(coursePk1, { code: n.code, description: n.description });
                } catch {
                    // best-effort
                }
            }
            return toCreate.length;
        } catch {
            return 0;
        }
    }

    // Save behavior:
    // - For existing courses where status changed: PATCH
    // - For new courses: PUT each; then ensure grade notations
    async function saveAll() {
        setInlineError("");
        setInlineSuccess("");

        try {
            const initialSet = initialCoursePk1SetRef.current || new Set();
            const initialMap = initialAssociationsMapRef.current || new Map();

            const current = associations.map((a) => ({ coursePk1: a.coursePk1, status: normalizeStatus(a.status) }));
            const newlyAdded = current.filter((a) => !initialSet.has(a.coursePk1));
            const changedExisting = current.filter((a) => initialSet.has(a.coursePk1) && (initialMap.get(a.coursePk1) || "") !== a.status);

            const patchOps = changedExisting.map(({ coursePk1, status }) =>
                backend
                    .patchAssociationStatus(coursePk1, status)
                    .then(() => ({ ok: true }))
                    .catch((e) => ({ ok: false, err: e?.response?.data?.message || e.message || String(e) }))
            );

            const putOps = newlyAdded.map(({ coursePk1, status }) =>
                backend
                    .putAssociation(coursePk1, status)
                    .then(async () => {
                        const createdNotations = await ensureRequiredNotations(coursePk1);
                        return { ok: true, createdNotations };
                    })
                    .catch((e) => ({ ok: false, err: e?.response?.data?.message || e.message || String(e) }))
            );

            const [patchResults, putResults] = await Promise.all([Promise.allSettled(patchOps), Promise.allSettled(putOps)]);

            let failures = [];
            let createdNotationTotal = 0;

            for (const r of patchResults) {
                if (r.status === "fulfilled") {
                    if (!r.value?.ok) failures.push(r.value);
                } else failures.push({ ok: false, err: r.reason });
            }
            for (const r of putResults) {
                if (r.status === "fulfilled") {
                    if (!r.value?.ok) failures.push(r.value);
                    else createdNotationTotal += r.value.createdNotations || 0;
                } else failures.push({ ok: false, err: r.reason });
            }

            await loadAll();

            if (failures.length > 0) {
                const firstErr = failures[0]?.err || M.notices.failedSave;
                setInlineError(firstErr);
                const extra = createdNotationTotal > 0 ? ` ${M.notices.ensureNotations(createdNotationTotal)}` : "";
                setInlineSuccess(`${M.notices.savedWarn}${extra ? " " + extra : ""}`);
            } else {
                const extra = createdNotationTotal > 0 ? ` ${M.notices.ensureNotations(createdNotationTotal)}` : "";
                setInlineSuccess(`${M.notices.savedAll}${extra ? " " + extra : ""}`);
            }
            setDirty(false);
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || M.notices.failedSave);
        }
    }

    function removeAssociation(coursePk1) {
        setInlineSuccess("");
        setInlineError("");
        const ok = window.confirm(M.confirm.remove);
        if (!ok) return;

        backend
            .deleteAssociation(coursePk1)
            .then(() => {
                setAssociations((prev) => prev.filter((a) => a.coursePk1 !== coursePk1));
                setInlineSuccess(M.notices.removed);
                setDirty(true);
            })
            .catch((err) => {
                setInlineError(err?.response?.data?.message || err.message || M.notices.failedRemove);
            });
    }

    function updateStatus(coursePk1, status) {
        setAssociations((prev) => prev.map((a) => (a.coursePk1 === coursePk1 ? { ...a, status: normalizeStatus(status) } : a)));
        setInlineSuccess("");
        setInlineError("");
        setDirty(true);
    }

    async function doSearch() {
        const term = (searchTerm || "").trim();
        if (!term) {
            setSearchResults([]);
            setSearchError("");
            return;
        }
        setSearchLoading(true);
        setSearchError("");
        setInlineError("");
        setInlineSuccess("");

        try {
            const [byName, byCourseId] = await Promise.allSettled([learn.searchCoursesByNameV3(term), learn.searchCoursesByCourseIdV3(term)]);
            const arr1 = byName.status === "fulfilled" ? byName.value : [];
            const arr2 = byCourseId.status === "fulfilled" ? byCourseId.value : [];

            const merged = uniqBy([...arr1, ...arr2], (x) => x && (x.id || x.pk1))
                .map((c) => ({
                    id: c?.id || c?.pk1,
                    courseId: c?.courseId || c?.externalId || "",
                    name: c?.name || c?.title || "(untitled course)",
                    availability: c?.availability?.available || c?.availability?.availability || "",
                }))
                .filter((c) => c.id);

            setSearchResults(merged);
            if (merged.length === 0) {
                const errMsg =
                    byName.status === "rejected" && byCourseId.status === "rejected"
                        ? byName.reason?.message || byCourseId.reason?.message || M.search.failed
                        : M.table.noResults;
                setSearchError(errMsg);
            }
        } catch (err) {
            setSearchError(err?.response?.data?.message || err.message || M.search.failed);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    }

    function addAssociation(course) {
        if (!course?.id) return;
        setAssociations((prev) => {
            const exists = prev.some((a) => a.coursePk1 === course.id);
            return exists ? prev : [...prev, { coursePk1: course.id, status: "Active" }];
        });
        setCourseInfo((prev) => ({ ...prev, [course.id]: { name: course.name, courseId: course.courseId } }));
        setInlineError("");
        setInlineSuccess(`${M.buttons.addActive}: ${course.courseId || course.name}`);
        setDirty(true);
    }

    /** ---------------- Gates ---------------- */
    if (!roleChecked) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.gate.checking}</div>
            </div>
        );
    }
    if (!hasAdminRole) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>{M.gate.blockedTitle}</h2>
                    <p style={{ fontSize: 12 }}>{M.gate.blockedMsg}</p>
                </div>
            </div>
        );
    }
    if (loading) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.notices.loading}</div>
            </div>
        );
    }
    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>{M.headings.error}</h2>
                    <p style={{ fontSize: 12 }}>{fatalError}</p>
                </div>
            </div>
        );
    }

    /** ---------------- Main UI ---------------- */
    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* Associations */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between" }}>
                    <h2 style={{ margin: 0 }}>{M.headings.current}</h2>
                    <button className="bb-button" onClick={() => loadAll({ clearMessages: true })} title={M.buttons.refresh}>
                        {M.buttons.refresh}
                    </button>
                </div>

                <div style={{ marginTop: 8 }}>
                    <div className="bb-row" style={{ marginBottom: 6 }}>
                        <button className="bb-button" onClick={saveAll} disabled={!dirty} aria-disabled={!dirty}>
                            {dirty ? M.buttons.saveChanges : M.buttons.saved}
                        </button>
                    </div>
                    <Flash type="error">{inlineError}</Flash>
                    <Flash type="success">{inlineSuccess}</Flash>

                    <table className="bb-table" role="grid">
                        <thead>
                        <tr>
                            <th>{M.table.name}</th>
                            <th>{M.table.id}</th>
                            <th>{M.table.status}</th>
                            <th style={{ width: 90 }}>{M.table.actions}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {associations.length === 0 ? (
                            <tr>
                                <td colSpan={4} style={{ textAlign: "center" }}>
                                    {M.table.noRows}
                                </td>
                            </tr>
                        ) : (
                            associations.map((a) => {
                                const info = courseInfo[a.coursePk1] || {};
                                return (
                                    <tr key={a.coursePk1}>
                                        <td>{info.name || "(loading…)"}</td>
                                        <td>{info.courseId || "-"}</td>
                                        <td>
                                            <select
                                                className="bb-select"
                                                value={normalizeStatus(a.status)}
                                                onChange={(e) => updateStatus(a.coursePk1, e.target.value)}
                                                aria-label={`${M.table.status} ${info.courseId || info.name || a.coursePk1}`}
                                            >
                                                <option value="Active">{M.status.active}</option>
                                                <option value="Inactive">{M.status.inactive}</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="bb-button danger"
                                                onClick={() => removeAssociation(a.coursePk1)}
                                                title={M.buttons.remove}
                                                aria-label={`${M.buttons.remove} ${info.courseId || info.name || a.coursePk1}`}
                                            >
                                                {M.buttons.remove}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add association */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between" }}>
                    <h2 style={{ margin: 0 }}>{M.headings.add}</h2>
                    <button className="bb-button" onClick={() => setOpenAdd((o) => !o)} aria-expanded={openAdd}>
                        {openAdd ? M.buttons.hide : M.buttons.show}
                    </button>
                </div>

                {openAdd && (
                    <div style={{ marginTop: 8 }}>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                doSearch();
                            }}
                            style={{ marginBottom: 6 }}
                            noValidate
                        >
                            <div className="bb-row">
                                <input
                                    type="text"
                                    placeholder={M.search.placeholder}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            doSearch();
                                        }
                                    }}
                                    autoComplete="off"
                                    spellCheck={false}
                                    style={{ minWidth: 320 }}
                                />
                                <button type="submit" className="bb-button" disabled={searchLoading} aria-busy={searchLoading}>
                                    {searchLoading ? M.buttons.searching : M.buttons.search}
                                </button>
                                <button
                                    type="button"
                                    className="bb-button secondary"
                                    onClick={() => {
                                        setSearchTerm("");
                                        setSearchResults([]);
                                        setSearchError("");
                                    }}
                                    title={M.buttons.clear}
                                >
                                    {M.buttons.clear}
                                </button>
                                {searchError && <span style={{ color: "#a40000", fontSize: 12 }}>{searchError}</span>}
                            </div>
                        </form>

                        <table className="bb-table" role="grid">
                            <thead>
                            <tr>
                                <th>{M.table.name}</th>
                                <th>{M.table.id}</th>
                                <th>{M.table.availability}</th>
                                <th style={{ width: 120 }}>{M.buttons.addActive}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {searchResults.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: "center" }}>
                                        {searchLoading ? M.buttons.searching : M.table.noResults}
                                    </td>
                                </tr>
                            ) : (
                                searchResults.map((c) => {
                                    const already = associations.some((a) => a.coursePk1 === c.id);
                                    return (
                                        <tr key={c.id}>
                                            <td>{c.name}</td>
                                            <td>{c.courseId || "-"}</td>
                                            <td>{String(c.availability || "-")}</td>
                                            <td>
                                                <button
                                                    className="bb-button"
                                                    onClick={() => addAssociation(c)}
                                                    disabled={already}
                                                    title={already ? M.buttons.added : M.buttons.addActive}
                                                    aria-disabled={already}
                                                >
                                                    {already ? M.buttons.added : M.buttons.addActive}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GradeHurdleAdmin;
